import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Check, Loader2, ChevronDown, ListTodo, ChevronUp } from 'lucide-react';
import { Badge } from '../ui/badge';
import { tareasDespachoAPI } from '../../api';
import { toast } from '../../hooks/use-toast';

const SubTareasSection = ({ tipoTarea, tareaId, isExpanded, onToggle }) => {
  const [subtareas, setSubtareas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newSubtarea, setNewSubtarea] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isExpanded && tareaId) {
      loadSubtareas();
    }
  }, [isExpanded, tareaId, tipoTarea]);

  const loadSubtareas = async () => {
    setLoading(true);
    try {
      const res = await tareasDespachoAPI.subtareas.getByTarea(tipoTarea, tareaId);
      setSubtareas(res.data);
    } catch (error) {
      console.error('Error cargando sub-tareas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubtarea = async () => {
    if (!newSubtarea.trim()) return;
    
    setSaving(true);
    try {
      await tareasDespachoAPI.subtareas.create({
        tipo_tarea_padre: tipoTarea,
        tarea_padre_id: tareaId,
        descripcion: newSubtarea.trim(),
        orden: subtareas.length
      });
      setNewSubtarea('');
      loadSubtareas();
      toast({ title: 'Sub-tarea añadida' });
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear la sub-tarea', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSubtarea = async (subtarea) => {
    try {
      await tareasDespachoAPI.subtareas.completar(subtarea.id, !subtarea.completada);
      loadSubtareas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleDeleteSubtarea = async (subtareaId) => {
    if (!window.confirm('¿Eliminar esta sub-tarea?')) return;
    try {
      await tareasDespachoAPI.subtareas.delete(subtareaId);
      loadSubtareas();
      toast({ title: 'Sub-tarea eliminada' });
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const completadas = subtareas.filter(st => st.completada).length;
  const total = subtareas.length;

  return (
    <div className="mt-2">
      <button 
        onClick={onToggle}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
      >
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        <ListTodo className="w-4 h-4" />
        <span>Sub-tareas</span>
        {total > 0 && (
          <Badge className={completadas === total ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
            {completadas}/{total}
          </Badge>
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
          {loading ? (
            <div className="py-2 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando...
            </div>
          ) : (
            <>
              {subtareas.map((st) => (
                <div key={st.id} className="flex items-center gap-2 group">
                  <button 
                    onClick={() => handleToggleSubtarea(st)}
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      st.completada 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : 'border-gray-300 hover:border-green-500'
                    }`}
                  >
                    {st.completada && <Check className="w-3 h-3" />}
                  </button>
                  <span className={`flex-1 text-sm ${st.completada ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {st.descripcion}
                  </span>
                  <button 
                    onClick={() => handleDeleteSubtarea(st.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ))}

              <div className="flex items-center gap-2 mt-2">
                <input
                  type="text"
                  placeholder="Nueva sub-tarea..."
                  className="flex-1 text-sm border-0 border-b border-gray-200 focus:border-blue-500 outline-none py-1"
                  value={newSubtarea}
                  onChange={(e) => setNewSubtarea(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSubtarea()}
                />
                <button 
                  onClick={handleAddSubtarea}
                  disabled={saving || !newSubtarea.trim()}
                  className="p-1 hover:bg-blue-100 rounded disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 text-blue-600" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};


export default SubTareasSection;
